import { type ChangeEvent, type FocusEvent, useEffect } from 'react';

import { useAtom } from 'jotai';

import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import validatePostcode from '@core/Utils/Validation/ValidatePostcode';

const usePostcodeData = (isRequired: boolean) => {
  const [locationState, setLocationState] = useAtom(locationAtom);
  const [postcode, setPostcode] = useAtom(postcodeAtom);
  const { location, error: hasError } = locationState;

  const shouldTriggerValidation = (postcode: string, valid: boolean) => {
    if ((isRequired && !valid) || (!isRequired && postcode && !valid)) {
      return true;
    }
    return false;
  };

  const checkIsValid = async () => {
    const response = await validatePostcode(postcode);

    if (response) {
      const { isValid, location } = response;
      setLocationState({
        ...locationState,
        error: !!shouldTriggerValidation(postcode, isValid),
        isValid,
        ...(location && { location: { lat: location.lat, lon: location.lon } }),
      });
      setPostcode(postcode);
    }
  };

  useEffect(() => {
    if (!postcode || !postcode.trim()) {
      setLocationState({
        ...locationState,
        error: false,
      });
      return;
    }

    if (postcode) void checkIsValid();
  }, [postcode]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPostcode(event.target.value?.toUpperCase());
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (event.target.value) {
      setPostcode(event.target.value?.toUpperCase());
    }
    void checkIsValid();
  };

  return {
    postcode,
    location,
    error: hasError,
    handleChange,
    handleBlur,
  };
};

export default usePostcodeData;
